import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { CreateCompound, GetCompounds, ReceiveCompound } from 'src/app/shared/states/transactions/transactions.actions';
import { TransactionsState } from 'src/app/shared/states/transactions/transactions.state';
import { ICompoundIndexModel } from '../../interfaces/compound-index-model.interface';
import { SearchInputColorEnum } from 'src/app/shared/components/search-input/search-input.component';
import { filterTable } from 'src/app/shared/helpers/helpers';
import { GenericTableColumn } from 'src/app/shared/interfaces/genetic-table-column';
import { RowType } from 'src/app/shared/interfaces/raw-type.enum';
import { FieldType } from 'src/app/shared/interfaces/field-type.enum';
import { ICreateCompoundRequest } from '../../interfaces/create-compound-request';
import { PinService } from 'src/app/shared/services/pin.service';
import { GetCustomDrugs } from 'src/app/shared/states/drugs/drugs.actions';
import { DrugsState } from 'src/app/shared/states/drugs/drugs.state';
import { IReceiveCompoundDialogSubmittedEvent } from '../../interfaces/receive-compound-submitted-event.interface';
import { BackCountService } from 'src/app/shared/services/back-count.service';
import { take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ActivityTypesEnum } from 'src/app/shared/interfaces/ActivityTypesEnum';

@UntilDestroy()
@Component({
  selector: 'app-compounds',
  templateUrl: './compounds.component.html',
  styleUrls: ['./compounds.component.scss', './../../inventory.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompoundsComponent implements OnInit {
  @Select(TransactionsState.loading) loading$: Observable<boolean | undefined>;
  @Select(TransactionsState.compounds) compounds$: Observable<ICompoundIndexModel[]>;

  filtered$: BehaviorSubject<ICompoundIndexModel[]> = new BehaviorSubject([]);

  SearchInputColorEnum = SearchInputColorEnum;

  dataListLength: number = 0;
  createCompoundDialog$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  receiveCompoundDialog$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  createCompoundPayload: ICreateCompoundRequest;
  receiveCompoundPayload: IReceiveCompoundDialogSubmittedEvent;
  private createCompoundPinChannel: string = "createCompound";
  private receiveCompoundPinChannel: string = "receiveCompound";

  ActivityTypesEnum = ActivityTypesEnum;


  columns: GenericTableColumn<ICompoundIndexModel>[] = [
    new GenericTableColumn<ICompoundIndexModel>({
      header: 'Date/Time',
      field: 'transactionDate',
      RowType: RowType.Double,
      doubleRowField: {
        headingField: 'transactionDate',
        headingFieldType: FieldType.DateTime,
        secondaryField1: 'createdByFirstName',
        secondaryFieldType1: FieldType.Text,
        secondaryField2: 'createdByLastName',
        secondaryFieldType2: FieldType.LastName,
      },
      style: {width: '12%'}
    }),
    new GenericTableColumn<ICompoundIndexModel>({
      header: 'Name',
      field: 'friendlyName',
      RowType: RowType.Double,
      doubleRowField: {
        headingField: 'friendlyName',
        secondaryField1: 'packageNdc',
      },
      style: {width: '20%'}
    }),
    new GenericTableColumn<ICompoundIndexModel>({
      header: 'Package Size',
      field: 'packageSize',
      sortable: false,
      fieldType: FieldType.Number,
      style: {width: '140px'}
    }),
    new GenericTableColumn<ICompoundIndexModel>({
      header: 'Activity',
      field: 'activityType',
      sortable: false,
      fieldType: FieldType.ActivityBadge,
    }),
    new GenericTableColumn<ICompoundIndexModel>({
      header: 'LOT #',
      field: 'lot',
      sortable: false,
    }),
    new GenericTableColumn<ICompoundIndexModel>({
      header: 'Exp',
      field: 'expirationDate',
      sortable: false,
      fieldType: FieldType.Date,
    }),
    new GenericTableColumn<ICompoundIndexModel>({
      header: 'Qty Cmpd',
      field: 'addedQuantity',
      sortable: false,
      fieldType: FieldType.Number,
    }),
    new GenericTableColumn<ICompoundIndexModel>({
      header: 'Ingredients',
      field: 'ingredientsCount',
      sortable: false,
      fieldType: FieldType.Number,
    }),
    new GenericTableColumn<ICompoundIndexModel>({
      header: '',
      sortable: false,
      RowType: RowType.Template,
    }),
  ];

  /**
   *
   */
  constructor(
    private store: Store, 
    private cdr: ChangeDetectorRef, 
    private pinService: PinService, 
    private backCountService: BackCountService,
    private router: Router
  )
   {}

  ngOnInit(): void {
    this.store.dispatch(new GetCompounds());
    if (!this.store.selectSnapshot(DrugsState.customDrugs)?.length) {
      this.store.dispatch(new GetCustomDrugs());
      
    }
    this.compounds$
    .pipe(untilDestroyed(this))
    .subscribe((compounds) => {
      if (compounds) {
        this.filtered$.next(compounds);
        this.dataListLength = compounds?.length;
        this.cdr.markForCheck();
      }
    })

    this.pinService.validatePin$
    .pipe(untilDestroyed(this))
    .subscribe(res => {
      if (res?.success && res?.dialogId === this.createCompoundPinChannel) {
        this.createCompoundPayload.pin = res.pin;
        this.store.dispatch(new CreateCompound(this.createCompoundPayload))
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.pinService.closeDialog();
        });
      } else if (res?.success && res?.dialogId === this.receiveCompoundPinChannel) { 
          this.store.dispatch(new ReceiveCompound({...this.receiveCompoundPayload, pin: res.pin}))
          .pipe(take(1))
          .subscribe(() => {
            this.backCountService.handleBackCountPillUpdate([
              {
                isBackCountMatches: this.receiveCompoundPayload.isCompoundBackCountMatches
              }, 
             ...this.receiveCompoundPayload.ingredients
            ]);
            this.pinService.closeDialog();
          })
      }
    })
    
  }

  openDialog(): void {}

  searchValueChanged(val: string): void {
    const filtered = filterTable(
      this.store.selectSnapshot(TransactionsState.compounds),
      val,
      'friendlyName',
      'packageNdc'
    );
    this.filtered$.next(filtered);
  }

  createCompoundSubmitted(request: ICreateCompoundRequest): void {
    this.createCompoundPayload = request;
    this.createCompoundDialog$.next(false);
    this.pinService.openPinDialog(this.createCompoundPinChannel);
  }

  receiveCompoundDialogSubmitted(event: IReceiveCompoundDialogSubmittedEvent): void {
   this.receiveCompoundPayload = event;
   this.receiveCompoundDialog$.next(false);
   this.pinService.openPinDialog(this.receiveCompoundPinChannel);
  }

  openDetail(id: string): void {
    this.router.navigate(['inventory/compounds', id]);
  }
}
